import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import Loader from "./loader";
import {
  useSearchCustomFulfillmentMutation,
  usePushCustomFulfillmentOrderMutation,
} from "../redux/RTK/customFulfillmentSlice";
import {
  EMPTY_SEARCH_FORM,
  MONTH_OPTIONS,
  ORDER_STATUS_OPTIONS,
  ORDER_TYPE_OPTIONS,
  buildCustomFulfillmentSearchPayload,
  buildFulfillmentPushPayload,
  buildInitialRowActions,
  extractSearchResults,
  getDaysInMonth,
  getYearOptions,
} from "../constants/customFulfillment";

function ShippedTrackingModal({ order, trackingNumber, onTrackingChange, onClose, onSubmit, submitting }) {
  return (
    <div id="shippedTrackingModal" className="overlay">
      <div className="popup clearfix">
        <div className="body">
          <div className="group">
            <div className="span12">
              <h3>Ship Order {order?.orderNumber}</h3>
              <div className="overlay-content">
                <div className="field">
                  <label>Tracking Numbers</label>
                  <input
                    type="text"
                    value={trackingNumber}
                    onChange={(e) => onTrackingChange(e.target.value)}
                    placeholder="Enter comma separated tracking numbers"
                  />
                </div>
                <div className="right close-modal">
                  <button
                    type="button"
                    className="btn-blue right"
                    onClick={onSubmit}
                    disabled={submitting || !trackingNumber.trim()}
                  >
                    Submit
                  </button>
                  <button type="button" className="btn-red right" onClick={onClose}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CancelReasonModal({ order, comment, onCommentChange, onClose, onSubmit, submitting }) {
  return (
    <div id="cancelReasonModal" className="overlay">
      <div className="popup clearfix">
        <div className="body">
          <div className="group">
            <div className="span12">
              <h3>Cancel Order {order?.orderNumber}</h3>
              <div className="overlay-content">
                <div className="field">
                  <label>Comment</label>
                  <textarea
                    value={comment}
                    onChange={(e) => onCommentChange(e.target.value)}
                    rows={4}
                    placeholder="Enter cancel reason"
                  />
                </div>
                <div className="right close-modal">
                  <button
                    type="button"
                    className="btn-blue right"
                    onClick={onSubmit}
                    disabled={submitting || !comment.trim()}
                  >
                    Submit
                  </button>
                  <button type="button" className="btn-red right" onClick={onClose}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DatePartSelects({ prefix, form, onChange }) {
  const month = form[`${prefix}Month`];
  const day = form[`${prefix}Day`];
  const year = form[`${prefix}Year`];
  const daysInMonth = getDaysInMonth(month, year);
  const yearOptions = useMemo(() => getYearOptions(), []);

  return (
    <div className="cf-date-row">
      <select
        className="month"
        value={month}
        onChange={(e) => onChange(`${prefix}Month`, e.target.value)}
      >
        <option value="">Month</option>
        {MONTH_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <select
        className="day"
        value={day}
        onChange={(e) => onChange(`${prefix}Day`, e.target.value)}
      >
        <option value="">Day</option>
        {Array.from({ length: daysInMonth }, (_, index) => {
          const value = String(index + 1);
          return (
            <option key={value} value={value}>
              {value}
            </option>
          );
        })}
      </select>
      <select
        className="year"
        value={year}
        onChange={(e) => onChange(`${prefix}Year`, e.target.value)}
      >
        <option value="">Year</option>
        {yearOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function CustomFulfillment() {
  const { companyName, clientId, divisionNumber, companyDivId } = useSelector(
    (state) => state.auth
  );
  const environment = process.env.REACT_APP_ENV;
  const [formData, setFormData] = useState({ ...EMPTY_SEARCH_FORM });
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [rowActions, setRowActions] = useState({});
  const [cancelOrder, setCancelOrder] = useState(null);
  const [cancelComment, setCancelComment] = useState("");
  const [shippedOrder, setShippedOrder] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [searchCustomFulfillment, { isLoading }] =
    useSearchCustomFulfillmentMutation();
  const [pushCustomFulfillmentOrder, { isLoading: isPushing }] =
    usePushCustomFulfillmentOrderMutation();

  const goHome = () => {
    const redirectUrl =
      environment === "dev" || environment === "qa"
        ? `https://${environment}.hibbertordering.com/home`
        : `https://hibbertordering.com/home`;
    window.location.href = redirectUrl;
  };

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const next = { ...prev, [field]: value };
      if (field.endsWith("Month") || field.endsWith("Year")) {
        const prefix = field.startsWith("start") ? "start" : "end";
        const maxDay = getDaysInMonth(
          field.endsWith("Month") ? value : next[`${prefix}Month`],
          field.endsWith("Year") ? value : next[`${prefix}Year`]
        );
        if (Number(next[`${prefix}Day`]) > maxDay) {
          next[`${prefix}Day`] = "";
        }
      }
      return next;
    });
  };

  const handleClear = (e) => {
    e.preventDefault();
    setFormData({ ...EMPTY_SEARCH_FORM });
    setResults([]);
    setHasSearched(false);
    setRowActions({});
    setCancelOrder(null);
    setCancelComment("");
    setShippedOrder(null);
    setTrackingNumber("");
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    const payload = buildCustomFulfillmentSearchPayload(formData, {
      clientId,
      companyDivId,
    });

    try {
      const response = await searchCustomFulfillment(payload).unwrap();
      const nextResults = extractSearchResults(response);
      setResults(nextResults);
      setHasSearched(true);
      setRowActions(buildInitialRowActions(nextResults));
      if (nextResults.length === 0) {
        toast.info("No orders found");
      }
    } catch (err) {
      setResults([]);
      setHasSearched(true);
      if (err?.data?.errorMessage === "token is expired") {
        toast.error("Token Expired. Please login again.");
        goHome();
        return;
      }
      toast.error("Failed to search custom fulfillment orders");
    }
  };

  const resetRowAction = (rowKey) => {
    setRowActions((prev) => ({ ...prev, [rowKey]: "" }));
  };

  const setActionForRelatedRows = (row, action) => {
    setRowActions((prev) => {
      const next = { ...prev };
      results.forEach((item) => {
        const sameOrder =
          (row.orderEntryId && item.orderEntryId === row.orderEntryId) ||
          item.orderNumber === row.orderNumber;
        if (sameOrder) {
          next[item.rowKey] = action;
        }
      });
      return next;
    });
  };

  const getApiErrorMessage = (err) =>
    err?.data?.errorMessage ||
    err?.data?.message ||
    err?.data?.error ||
    err?.error ||
    err?.message ||
    "Request failed";

  const submitFulfillmentAction = async (
    row,
    currentOrderStatus,
    orderComment,
    trackingNumberValue = ""
  ) => {
    const payload = buildFulfillmentPushPayload(row, {
      currentOrderStatus,
      orderComment,
      companyNumber: clientId,
      trackingNumbers: trackingNumberValue,
    });
    await pushCustomFulfillmentOrder(payload).unwrap();
  };

  const handleRowAction = (row, value) => {
    setRowActions((prev) => ({ ...prev, [row.rowKey]: value }));
    if (value === "Shipped") {
      setShippedOrder(row);
      setTrackingNumber("");
      return;
    }
    if (value === "Cancel") {
      setCancelOrder(row);
      setCancelComment("");
    }
  };

  const handleShippedSubmit = async () => {
    if (!shippedOrder || !trackingNumber.trim()) return;
    const row = shippedOrder;
    const tracking = trackingNumber.trim();
    setShippedOrder(null);
    setTrackingNumber("");
    try {
      await submitFulfillmentAction(row, "SHIPPED", "fulfilled", tracking);
      toast.success("success");
      setActionForRelatedRows(row, "Shipped");
    } catch (err) {
      toast.error(getApiErrorMessage(err));
      resetRowAction(row.rowKey);
    }
  };

  const handleShippedClose = () => {
    if (shippedOrder) {
      resetRowAction(shippedOrder.rowKey);
    }
    setShippedOrder(null);
    setTrackingNumber("");
  };

  const handleCancelSubmit = async () => {
    if (!cancelOrder || !cancelComment.trim()) return;
    const row = cancelOrder;
    const comment = cancelComment.trim();
    setCancelOrder(null);
    setCancelComment("");
    try {
      await submitFulfillmentAction(row, "CANCEL", comment);
      toast.success("success");
      setActionForRelatedRows(row, "Cancel");
    } catch (err) {
      toast.error(getApiErrorMessage(err));
      resetRowAction(row.rowKey);
    }
  };

  const handleCancelClose = () => {
    if (cancelOrder) {
      resetRowAction(cancelOrder.rowKey);
    }
    setCancelOrder(null);
    setCancelComment("");
  };

  return (
    <div>
      {(isLoading || isPushing) && <Loader />}
      <ToastContainer position="top-right" autoClose={4000} />
      <div className="container">
        <div className="group">
          <div className="nav2 span12">
            <div className="user">
              <em style={{ fontSize: "18px" }} className="fa fa-user"></em>{" "}
              {clientId} - {divisionNumber} | {companyName}{" "}
            </div>
            <div className="bread-crumb">
              <Link to="" onClick={goHome}>
                <i style={{ fontSize: "18px" }} className="fa fa-home"></i> Home
              </Link>{" "}
              / Custom Fulfillment
            </div>
          </div>
        </div>
      </div>
      <div className="container">
        <h1>Custom Fulfillment</h1>
        <article className="card">
          <header>
            <i className="fa fa-search"></i>
            <label>Search</label>
          </header>
          <form className="body" onSubmit={handleSearch}>
            <div className="group">
              <div className="span6">
                <div className="field">
                  <input
                    type="text"
                    placeholder="Order Number"
                    value={formData.orderNumber}
                    onChange={(e) => handleChange("orderNumber", e.target.value)}
                  />
                </div>
              </div>
              <div className="span6">
                <div className="field">
                  <input
                    type="text"
                    placeholder="Ship Last Name"
                    value={formData.shipLastName}
                    onChange={(e) =>
                      handleChange("shipLastName", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>
            <div className="group">
              <div className="span6">
                <div className="field">
                  <input
                    type="text"
                    placeholder="Customer Order Number"
                    value={formData.customerOrderNumber}
                    onChange={(e) =>
                      handleChange("customerOrderNumber", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="span6">
                <div className="field">
                  <select
                    value={formData.orderStatus}
                    onChange={(e) =>
                      handleChange("orderStatus", e.target.value)
                    }
                  >
                    <option value="">Select Order Status</option>
                    {ORDER_STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="group">
              <div className="span6">
                <div className="field">
                  <input
                    type="text"
                    placeholder="Item Code"
                    value={formData.itemCode}
                    onChange={(e) => handleChange("itemCode", e.target.value)}
                  />
                </div>
              </div>
              <div className="span6">
                <div className="field">
                  <select
                    value={formData.orderType}
                    onChange={(e) => handleChange("orderType", e.target.value)}
                  >
                    <option value="">Select Order Type</option>
                    {ORDER_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
            <div className="group">
              <div className="span6">
                <div className="field">
                  <input
                    type="text"
                    placeholder="Ship First Name"
                    value={formData.shipFirstName}
                    onChange={(e) =>
                      handleChange("shipFirstName", e.target.value)
                    }
                  />
                </div>
              </div>
              <div className="span6">
                <div className="field cf-date-field">
                  <label>Start Date</label>
                  <DatePartSelects
                    prefix="start"
                    form={formData}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className="group">
              <div className="span6"></div>
              <div className="span6">
                <div className="field cf-date-field">
                  <label>End Date</label>
                  <DatePartSelects
                    prefix="end"
                    form={formData}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
            <div className="group">
              <div className="span12">
                <button className="btn-blue right" type="submit">
                  Search
                </button>
                <button className="btn-red right" type="button" onClick={handleClear}>
                  Clear
                </button>
              </div>
            </div>
          </form>
        </article>

        {hasSearched && results.length > 0 && (
          <article className="card">
            <header>
              <i className="fa fa-list"></i>
              <label>Search Results</label>
            </header>
            <div className="body">
              <div className="table-wrapper">
                <table className="results-table">
                  <thead>
                    <tr>
                      <th>Order Number</th>
                      <th>Customer Order Number</th>
                      <th>Item Code</th>
                      <th>Ship Name</th>
                      <th>Ship Address</th>
                      <th>Status</th>
                      <th>Type</th>
                      <th>Order Date</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((order) => (
                      <tr key={order.rowKey}>
                        <td>{order.orderNumber}</td>
                        <td>{order.customerOrderNumber}</td>
                        <td>{order.itemCode}</td>
                        <td>
                          {[order.shipFirstName, order.shipLastName]
                            .filter(Boolean)
                            .join(" ")}
                        </td>
                        <td>{order.shipAddress}</td>
                        <td>{order.status}</td>
                        <td>{order.orderType}</td>
                        <td>{order.createdOn}</td>
                        <td>
                          <select
                            className="cf-action-select"
                            value={rowActions[order.rowKey] || ""}
                            onChange={(e) => handleRowAction(order, e.target.value)}
                          >
                            <option value="">Select</option>
                            <option value="Shipped">Shipped</option>
                            <option value="Cancel">Cancel</option>
                          </select>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </article>
        )}
      </div>
      {shippedOrder && (
        <ShippedTrackingModal
          order={shippedOrder}
          trackingNumber={trackingNumber}
          onTrackingChange={setTrackingNumber}
          onClose={handleShippedClose}
          onSubmit={handleShippedSubmit}
          submitting={isPushing}
        />
      )}
      {cancelOrder && (
        <CancelReasonModal
          order={cancelOrder}
          comment={cancelComment}
          onCommentChange={setCancelComment}
          onClose={handleCancelClose}
          onSubmit={handleCancelSubmit}
          submitting={isPushing}
        />
      )}
    </div>
  );
}

export default CustomFulfillment;
